export const MAX_FILE_MB = 100

const MAX_FILE_BYTES = MAX_FILE_MB * 1024 * 1024

export function validateFileSize(file: File): boolean {
  return file.size <= MAX_FILE_BYTES
}

export function splitFilename(originalName: string): string {
  const dotIndex = originalName.lastIndexOf('.')
  const base = dotIndex !== -1 ? originalName.slice(0, dotIndex) : originalName
  return `${base}-pages.pdf`
}

export type PageRangeParseResult =
  | { ok: true; pageIndices: number[] }
  | { ok: false; code: 'empty' | 'syntax' | 'out_of_range' | 'reversed_range' }

/**
 * Parse a page spec like "1-3, 7, 10-12" into 0-based indices.
 * The output order follows the user's input order.
 */
export function parsePageRanges(spec: string, totalPages: number): PageRangeParseResult {
  const trimmed = spec.trim()
  if (!trimmed) return { ok: false, code: 'empty' }

  const pageIndices: number[] = []
  for (const part of trimmed.split(',')) {
    const piece = part.trim()
    if (!piece) return { ok: false, code: 'syntax' }

    const rangeMatch = piece.match(/^(\d+)\s*-\s*(\d+)$/)
    if (rangeMatch) {
      const from = Number(rangeMatch[1])
      const to = Number(rangeMatch[2])
      if (from < 1 || to < 1 || from > totalPages || to > totalPages) {
        return { ok: false, code: 'out_of_range' }
      }
      if (from > to) return { ok: false, code: 'reversed_range' }
      for (let page = from; page <= to; page += 1) {
        pageIndices.push(page - 1)
      }
      continue
    }

    if (!/^\d+$/.test(piece)) return { ok: false, code: 'syntax' }
    const page = Number(piece)
    if (page < 1 || page > totalPages) {
      return { ok: false, code: 'out_of_range' }
    }
    pageIndices.push(page - 1)
  }

  if (pageIndices.length === 0) return { ok: false, code: 'empty' }
  return { ok: true, pageIndices }
}

export async function getPdfPageCount(file: File): Promise<number> {
  const { PDFDocument } = await import('pdf-lib')
  const bytes = await file.arrayBuffer()
  const pdfDoc = await PDFDocument.load(bytes)
  return pdfDoc.getPageCount()
}

export async function extractPdfPages(
  file: File,
  pageIndices: number[],
): Promise<{ blob: Blob; selectedPages: number }> {
  const { PDFDocument } = await import('pdf-lib')

  const bytes = await file.arrayBuffer()
  const sourcePdf = await PDFDocument.load(bytes)
  const outPdf = await PDFDocument.create()
  const copiedPages = await outPdf.copyPages(sourcePdf, pageIndices)

  for (const page of copiedPages) {
    outPdf.addPage(page)
  }

  const out = await outPdf.save()
  return {
    blob: new Blob([out], { type: 'application/pdf' }),
    selectedPages: pageIndices.length,
  }
}
